/**
 * @fileoverview System audit log service — local-first read/write of audit logs for system-web.
 * @module services/systemAuditLogService
 *
 * Responsibilities:
 * - Write audit log entries to Dexie immediately, then push to the API in the background.
 * - Serve paginated audit log lists and per-document audit history from the local cache.
 * - Track the current system-admin actor (set/clear/get) for audit stamping.
 *
 * Dependencies:
 * - @firesquirrel/local-db (getSystemDb, wakeLocalWrite)
 * - packages/audit (calculateAuditChanges, createAuditClient)
 * - ../apiClient (apiFetch for background log sync)
 *
 * Architecture:
 * - READ → Dexie audit_logs table (instant, offline-capable).
 * - SYNC → Background pull from API writes server entries into Dexie.
 * - WRITE → writeAuditLog() writes to Dexie immediately, then fires a
 *   background POST so the server record is also created.
 *
 * @localFirst
 * Source of Truth: local (Dexie)
 * Write Flow: Dexie write → wakeLocalWrite → sync push to MongoDB
 * Sync Strategy: Optimistic write; background pull reconciles server entries
 * Conflict Resolution: Last-write-wins (server timestamp)
 *
 * Notes:
 * - Mirrors web/tenant-web/src/services/auditLogService.ts — same pattern,
 *   different DB (getSystemDb instead of getTenantDb), different source.app.
 * - Called directly from every mutating system-web service function.
 */

import { getSystemDb, wakeLocalWrite } from '@firesquirrel/local-db';
import { calculateAuditChanges, createAuditClient } from '../../../packages/audit/src';
import type {
  AuditLogEntry,
  AuditAction,
  ChangeDetail,
  WriteAuditLogOptions,
  AuditLogFetchOptions,
  AuditLogFetchResult,
  AuditLogListParams,
  AuditLogListResult,
} from '../../../packages/audit/src';
import { apiFetch } from '../apiClient';

export type { AuditLogEntry, AuditAction, ChangeDetail };

export const computeAuditChanges = calculateAuditChanges;

let _currentUser: { userId: string; email: string; name: string } | null = null;

const systemAuditClient = createAuditClient({
  getDb: getSystemDb,
  sourceApp: 'system-admin',
  defaultPerformedBy: { userId: 'local', email: '', name: 'System Admin' },
  getCurrentUser: () => _currentUser,
  requireUserForWrites: true,
  onLocalWrite: () => {
    wakeLocalWrite('audit_logs');
  },
  fetchRemoteLogs: (params) => apiFetch(`/api/audit-logs?${params.toString()}`),
});

export function setSystemAuditUser(user: { userId: string; email: string; name: string }): void {
  _currentUser = user;
}

export function clearSystemAuditUser(): void {
  _currentUser = null;
}

export function getSystemAuditUser(): { userId: string; email: string; name: string } | null {
  return _currentUser;
}

export function assertSystemAuditActorReady(opts: Pick<WriteAuditLogOptions, 'collection' | 'action' | 'performedBy' | 'metadata'>): void {
  systemAuditClient.assertCanWriteAuditLog(opts);
}

/**
 * Write an audit log entry — local-first.
 * Immediately writes to Dexie so it appears in the UI without a round-trip,
 * then fires a background POST to persist on the server.
 */
export async function writeAuditLog(opts: WriteAuditLogOptions): Promise<void> {
  await systemAuditClient.writeAuditLog(opts);
}

/**
 * General-purpose paginated audit log list — Dexie first.
 * Filters by collection, action, and userId. Used by AuditLogsPage.
 */
export async function listAuditLogs(params: AuditLogListParams = {}): Promise<AuditLogListResult> {
  return systemAuditClient.listAuditLogs(params);
}

export async function fetchAuditLogs(
  collection: string,
  documentId: string,
  options: AuditLogFetchOptions = {},
): Promise<AuditLogFetchResult> {
  return systemAuditClient.fetchAuditLogs(collection, documentId, options);
}
